
import type { PropType, SetupContext } from 'vue'
import type { ToolRestrictedFieldMessages } from '../../models/i18n/tools.messages'
import { computed, defineComponent, getCurrentInstance } from 'vue'
import { useToolingRestricted } from '../../composables/use-tooling-restricted'
import { RestrictedValueEnum } from '../../enums/restricted-value.enum'
import { EpbToggle } from '../shared/epb-toggle'

export enum ToolingRestrictedToggleEnum {
  LOCK = 'lock',
  UNLOCK = 'unlock',
}

export default defineComponent({
  name: 'ToolingRestrictedToggle',
  components: { EpbToggle },
  props: {
    messages: {
      type: Object as PropType<ToolRestrictedFieldMessages>,
      required: true,
    },
    value: {
      type: Array<string>,
      required: true,
    },
    disabled: Boolean,
  },
  emits: ['change'],
  setup(props, ctx: SetupContext) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const { getRestrictedValue, userRoles } = useToolingRestricted()

    const options = [
      {
        id: ToolingRestrictedToggleEnum.LOCK,
        content: 'lock',
      },
      {
        id: ToolingRestrictedToggleEnum.UNLOCK,
        content: 'unlock',
      },
    ]

    const selectedOption = computed(() => {
      const value = getRestrictedValue(props.value)

      if (value === RestrictedValueEnum.NONE) {
        return ToolingRestrictedToggleEnum.UNLOCK
      }

      return ToolingRestrictedToggleEnum.LOCK
    })

    const restrictedTooltip = computed((): string => {
      const defaultRole = props.messages.roles.default.toUpperCase()
      if (selectedOption.value === ToolingRestrictedToggleEnum.UNLOCK) {
        return $epb.utils.compileMessage(props.messages.values[RestrictedValueEnum.READ], {
          roles: defaultRole,
        })
      }
      const readMessage: string = $epb.utils.compileMessage(
        props.messages.values[RestrictedValueEnum.READ],
        { roles: defaultRole },
      )
      const writeMessage: string = $epb.utils.compileMessage(
        props.messages.values[RestrictedValueEnum.WRITE],
        { roles: props.value.join(', ') },
      )
      return `${readMessage}<br>${writeMessage}`
    })

    const onToggleChange = (value: ToolingRestrictedToggleEnum): void => {
      const roles = value === ToolingRestrictedToggleEnum.UNLOCK ? [] : userRoles.value
      ctx.emit('change', roles)
    }

    return {
      baseClass: 'epb-tooling-restricted-toggle',
      restrictedTooltip,
      options,
      selectedOption,
      onToggleChange,
    }
  },
})
